import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   IMAGE PATHS  (files live in  src/assets/images/ )
   Put your pictures in that folder with the SAME file names below.
   If a picture is missing the page still works (navy background shows).
------------------------------------------------------------------- */
const imageFiles = require.context("../assets/images", false, /\.(png|jpe?g|webp|svg)$/);
const pic = (name) => {
  try { return imageFiles(`./${name}`); } catch (e) { return undefined; }
};
const images = {
  banner: pic("about-banner.png"), // 1920 x 600
  story: pic("about-story.png"),   // 1100 x 900
  team: pic("about-team.png"),     // 1000 x 1100
  director: pic("about-director.png"), // 800 x 1000 (portrait)
  cta: pic("home-cta.png"),        // same picture as Home page CTA (1920 x 700)
};
const bg = (overlay, img) => (img ? `${overlay}, url(${img})` : overlay);

/* ------------------------------------------------------------------
   DIRECTOR DETAILS  (change the name and title here)
------------------------------------------------------------------- */
const DIRECTOR = {
  name: "Ussama Fahad",
  title: "Director, Guardian Facility Services Ltd",
};

/* ------------------------------------------------------------------
   ICONS (inline SVG, no extra packages needed)
------------------------------------------------------------------- */
const paths = {
  wrench: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
  sparkles: (<><path d="M11 3l1.9 5.1L18 10l-5.1 1.9L11 17l-1.9-5.1L4 10l5.1-1.9z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" /></>),
  pound: <path d="M18 7c0-5.333-8-5.333-8 0M10 7v14M6 21h12M6 13h10" />,
  building: (<><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" /></>),
  shield: (<><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></>),
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  layers: <path d="M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />,
  pin: (<><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></>),
  key: (<><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.8 12.2 21 2M17 6l3 3M14 9l2.5 2.5" /></>),
  user: (<><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></>),
  quote: <path d="M10 11H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6c0 2.5-1.5 4-4 5M20 11h-4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6c0 2.5-1.5 4-4 5" />,
  hash: <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />,
};

function Icon({ name, size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

/* Light scroll animation: elements with class "ab-rv" fade up when they scroll into view.
   Anything already visible on load is never hidden, and nothing is hidden if the browser lacks support. */
function useReveal() {
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".ab-rv").forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight * 0.92) {
        el.classList.add("ab-wait");
        io.observe(el);
      }
    });
    return () => io.disconnect();
  }, []);
}

/* ------------------------------------------------------------------
   PAGE CONTENT
------------------------------------------------------------------- */
const facts = [
  { icon: "pin", label: "Registered in", value: "England and Wales" },
  { icon: "hash", label: "Company No.", value: "17521897" },
  { icon: "building", label: "SIC 81100", value: "Combined facilities support" },
  { icon: "building", label: "SIC 98000", value: "Residents property management" },
];

const directorMessage = [
  "Welcome to Guardian Facility Services Ltd.",
  "We started this company on a simple idea: looking after a property should never mean chasing several different providers. Whether you are a landlord, a tenant, a homeowner or a business owner, you should be able to rely on one dependable team to manage your property and keep it in excellent condition.",
  "Our work is built on two areas of expertise. The first is residents property management, where we handle lettings and tenancy administration in a fair, transparent and compliant way. The second is combined facilities support, the maintenance, repairs and professional cleaning that keep homes and workplaces safe, comfortable and presentable.",
  "Every member of our team is trained, background-checked and insured, and we hold ourselves to clear standards: honest communication, transparent costs and work done properly the first time.",
  "Thank you for considering Guardian Facility Services. We look forward to caring for your property with the same attention we would give our own.",
];

const directorFocus = [
  { icon: "key", label: "Residents property management", code: "SIC 98000" },
  { icon: "building", label: "Combined facilities support", code: "SIC 81100" },
];

const vision = [
  { icon: "wrench", title: "Functional", text: "Repairs, upkeep and safety checks that keep every property working as it should." },
  { icon: "sparkles", title: "Beautiful", text: "High-standard cleaning and care that keeps homes and workplaces looking their best." },
  { icon: "pound", title: "Profitable", text: "Well-managed lettings and maintained buildings that protect your investment." },
];

const trust = [
  { icon: "layers", title: "The Complete Package", text: "No need to hire a letting agent, a handyman and a cleaning company separately. We do it all." },
  { icon: "building", title: "Residential and Commercial Experts", text: "We adapt our standards to match the comfort of a home or the strict regulations of a business workspace." },
  { icon: "shield", title: "Vetted and Insured Team", text: "All our cleaning staff and maintenance engineers are fully trained, background-checked and insured." },
  { icon: "clock", title: "24/7 Emergency Support", text: "We look after our landlords and tenants with rapid maintenance responses when things go wrong." },
];

/* ------------------------------------------------------------------
   PAGE CSS (everything for the About page is in this file)
------------------------------------------------------------------- */
const css = `
.ab-banner{background-color:var(--navy-deep);background-size:cover;background-position:center;color:#fff;padding-top:110px;padding-bottom:110px}
.ab-banner-inner{max-width:720px;animation:abRise .8s ease-out both}
@keyframes abRise{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
.ab-banner h1{color:#fff;font-size:clamp(32px,4.8vw,52px);font-weight:800;letter-spacing:-.5px;margin-bottom:16px}
.ab-banner p{font-size:20px;font-style:italic;color:#DCE3EA;margin:0;max-width:600px}

.ab-rv{transition:opacity .75s ease var(--d,0s),transform .75s ease var(--d,0s)}
.ab-rv.ab-wait{opacity:0;transform:translateY(28px)}
.ab-rv.ab-wait.in{opacity:1;transform:none}

.ab-ico{flex:0 0 auto;width:52px;height:52px;border-radius:50%;background:var(--mist);color:var(--navy);display:grid;place-items:center}

.ab-section{padding-top:96px;padding-bottom:96px}
.ab-head{max-width:720px;margin-bottom:48px}
.ab-head.center{margin-left:auto;margin-right:auto;text-align:center}
.ab-head h2{font-size:clamp(28px,3.6vw,40px);font-weight:800}
.ab-head p{color:var(--muted);font-size:18px;margin:0}

.ab-story{display:grid;grid-template-columns:1.1fr 1fr;gap:72px;align-items:center}
.ab-story h2{font-size:clamp(28px,3.6vw,40px);font-weight:800}
.ab-story p{color:var(--muted);font-size:18px}
.ab-story-img{position:relative;padding:0 28px 28px 0}
.ab-story-img::before{content:"";position:absolute;right:0;bottom:0;width:72%;height:72%;background:var(--navy);border-radius:6px}
.ab-story-img img{position:relative;width:100%;aspect-ratio:11/9;object-fit:cover;border-radius:6px;background:var(--mist)}

.ab-facts{margin-top:-8px;padding-bottom:96px}
.ab-facts-grid{display:grid;grid-template-columns:repeat(4,1fr);background:#fff;border:1px solid #E1E6EB;border-top:4px solid var(--gold);border-radius:6px}
.ab-fact{display:flex;gap:14px;align-items:center;padding:26px 24px}
.ab-fact + .ab-fact{border-left:1px solid var(--mist)}
.ab-fact small{display:block;font-size:13.5px;color:var(--muted);line-height:1.3}
.ab-fact strong{display:block;font-family:var(--font-head);color:var(--navy);font-size:16px;line-height:1.3}

.ab-dir{scroll-margin-top:82px;background:var(--paper);padding-top:96px;padding-bottom:96px;overflow:hidden}
.ab-dir-grid{display:grid;grid-template-columns:.85fr 1.15fr;gap:72px;align-items:center}
.ab-dir-photo{position:relative;padding:0 0 30px 30px}
.ab-dir-photo::before{content:"";position:absolute;left:0;bottom:0;width:78%;height:78%;border:3px solid var(--gold);border-radius:6px}
.ab-dir-frame{position:relative;aspect-ratio:4/5;border-radius:6px;overflow:hidden;background:var(--navy);box-shadow:0 22px 48px rgba(15,34,56,.22)}
.ab-dir-frame img{width:100%;height:100%;object-fit:cover;transition:transform 1.2s ease}
.ab-dir-photo:hover .ab-dir-frame img{transform:scale(1.04)}
.ab-dir-empty{position:absolute;inset:0;display:grid;place-items:center;color:rgba(255,255,255,.35)}
.ab-dir-plate{position:absolute;left:0;right:0;bottom:0;padding:70px 24px 22px;background:linear-gradient(180deg,rgba(15,34,56,0),rgba(15,34,56,.92));color:#fff}
.ab-dir-plate strong{display:block;font-family:var(--font-head);font-size:22px;line-height:1.2}
.ab-dir-plate span{display:block;font-size:15px;color:#C4CFDA;margin-top:4px}
.ab-dir-text h2{font-size:clamp(28px,3.6vw,40px);font-weight:800;margin-bottom:22px}
.ab-dir-quote{color:var(--gold);margin-bottom:10px;animation:abFloat 5s ease-in-out infinite}
@keyframes abFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
.ab-dir-text p{color:#3A4652;font-size:18px}
.ab-dir-text p:first-of-type{font-family:var(--font-head);font-weight:700;color:var(--navy);font-size:20px}
.ab-dir-sign{margin-top:28px;padding-top:22px;border-top:1px solid #D6DDE4;display:flex;gap:18px;align-items:center;flex-wrap:wrap}
.ab-dir-sign strong{display:block;font-family:var(--font-head);font-style:italic;font-size:24px;color:var(--navy)}
.ab-dir-sign span{color:var(--muted);font-size:15.5px}
.ab-dir-focus{display:flex;gap:12px;flex-wrap:wrap;margin-top:26px}
.ab-dir-chip{display:flex;gap:12px;align-items:center;padding:12px 16px;background:#fff;border:1px solid #E1E6EB;border-radius:6px;transition:transform .25s,box-shadow .25s}
.ab-dir-chip:hover{transform:translateY(-3px);box-shadow:0 12px 24px rgba(15,34,56,.1)}
.ab-dir-chip svg{color:var(--navy)}
.ab-dir-chip b{display:block;font-family:var(--font-head);font-size:15px;color:var(--navy);line-height:1.25}
.ab-dir-chip small{color:var(--muted);font-size:13px}

.ab-vision{background:var(--navy);color:#fff;padding-top:90px;padding-bottom:90px}
.ab-vision .ab-head h2{color:#fff}
.ab-vision .ab-head p{color:#C4CFDA}
.ab-vision-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px}
.ab-vision-item{transition:opacity .75s ease var(--d,0s),transform .75s ease var(--d,0s),background .3s,border-color .3s;padding:32px 28px;border:1px solid rgba(185,194,204,.3);border-radius:6px;background:rgba(255,255,255,.04)}
.ab-vision-item:hover{background:rgba(255,255,255,.08);border-color:rgba(201,162,75,.6)}
.ab-vision-item .ab-ico{background:rgba(201,162,75,.16);color:var(--gold);margin-bottom:18px}
.ab-vision-item h3{color:#fff;font-size:22px}
.ab-vision-item p{margin:0;color:#C4CFDA;font-size:16.5px}

.ab-trust{display:grid;grid-template-columns:.9fr 1.1fr;gap:64px;align-items:start}
.ab-trust-img{position:sticky;top:120px}
.ab-trust-img img{width:100%;aspect-ratio:10/11;object-fit:cover;border-radius:6px;background:var(--navy)}
.ab-trust-list{display:grid;gap:18px}
.ab-trust-card{display:flex;gap:18px;padding:26px 26px;background:#fff;border:1px solid #E1E6EB;border-radius:6px;transition:opacity .75s ease var(--d,0s),transform .75s ease var(--d,0s),box-shadow .2s,border-color .2s}
.ab-trust-card:hover{box-shadow:0 12px 28px rgba(15,34,56,.1);border-color:var(--silver)}
.ab-trust-card .ab-ico{background:var(--navy);color:#fff}
.ab-trust-card h3{font-size:19px;margin-bottom:6px}
.ab-trust-card p{margin:0;color:var(--muted);font-size:16.5px;line-height:1.55}

.ab-cta{padding-top:100px;padding-bottom:100px;text-align:center;color:#fff;background-color:var(--navy-deep);background-size:cover;background-position:center}
.ab-cta h2{color:#fff;font-size:clamp(28px,4vw,42px);font-weight:800;max-width:760px;margin:0 auto 16px}
.ab-cta p{color:#DCE3EA;font-size:19px;max-width:560px;margin:0 auto 34px}
.ab-cta-btns{display:flex;gap:14px;justify-content:center;flex-wrap:wrap}
.ab-cta .btn svg{vertical-align:-5px;margin-right:8px}

@media (max-width:980px){
  .ab-banner{padding-top:80px;padding-bottom:80px}
  .ab-section{padding-top:72px;padding-bottom:72px}
  .ab-story{grid-template-columns:1fr;gap:48px}
  .ab-facts{padding-bottom:72px}
  .ab-facts-grid{grid-template-columns:1fr 1fr}
  .ab-fact:nth-child(3){border-left:0}
  .ab-fact:nth-child(n+3){border-top:1px solid var(--mist)}
  .ab-vision-grid{grid-template-columns:1fr}
  .ab-dir{padding-top:72px;padding-bottom:72px}
  .ab-dir-grid{grid-template-columns:1fr;gap:48px}
  .ab-dir-photo{max-width:440px;margin:0 auto;width:100%}
  .ab-trust{grid-template-columns:1fr;gap:40px}
  .ab-trust-img{position:static}
  .ab-trust-img img{aspect-ratio:16/10}
}
@media (max-width:560px){
  .ab-facts-grid{grid-template-columns:1fr}
  .ab-fact + .ab-fact{border-left:0;border-top:1px solid var(--mist)}
  .ab-trust-card{flex-direction:column;gap:14px}
}
`;

/* ------------------------------------------------------------------
   COMPONENT
------------------------------------------------------------------- */
export default function About() {
  useReveal();

  /* Opens the Director section when arriving from the Home page ("/about#director") */
  useEffect(() => {
    if (window.location.hash !== "#director") return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("director");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <style>{css}</style>

      {/* BANNER */}
      <section
        className="ab-banner"
        style={{ backgroundImage: bg("linear-gradient(90deg, rgba(15,34,56,.93) 0%, rgba(15,34,56,.75) 55%, rgba(15,34,56,.45) 100%)", images.banner) }}
      >
        <div className="container">
          <div className="ab-banner-inner">
            <h1>About Guardian Facility Services Ltd</h1>
            <p>Keeping residential and commercial spaces moving, clean, and well-maintained.</p>
          </div>
        </div>
      </section>

      {/* OUR STORY & VISION */}
      <section className="ab-section container ab-story">
        <div className="ab-rv">
          <h2>Our Story and Vision</h2>
          <p>
            Founded to simplify how people look after their properties, Guardian Facility Services Ltd
            delivers seamless property letting, expert maintenance, and high-standard cleaning across
            the UK.
          </p>
          <p>
            We operate under SIC Codes 81100 and 98000, meaning we are officially structured to handle
            both the administrative side of renting and property management alongside the heavy
            lifting of physical facility support. From single flats to sprawling commercial premises,
            our goal is to keep properties functional, beautiful, and profitable.
          </p>
          <Link to="/services" className="btn btn-navy">See What We Do</Link>
        </div>
        <div className="ab-story-img ab-rv" style={{ "--d": ".15s" }}>
          <img src={images.story} alt="Guardian Facility Services at work" />
        </div>
      </section>

      {/* COMPANY FACTS */}
      <section className="ab-facts container">
        <div className="ab-facts-grid ab-rv">
          {facts.map((f) => (
            <div className="ab-fact" key={f.label}>
              <div className="ab-ico"><Icon name={f.icon} /></div>
              <div>
                <small>{f.label}</small>
                <strong>{f.value}</strong>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DIRECTOR MESSAGE */}
      <section className="ab-dir" id="director">
        <div className="container ab-dir-grid">
          <div className="ab-dir-photo ab-rv">
            <div className="ab-dir-frame">
              {images.director ? (
                <img src={images.director} alt={`${DIRECTOR.name}, ${DIRECTOR.title}`} />
              ) : (
                <div className="ab-dir-empty"><Icon name="user" size={120} /></div>
              )}
              <div className="ab-dir-plate">
                <strong>{DIRECTOR.name}</strong>
                <span>{DIRECTOR.title}</span>
              </div>
            </div>
          </div>
          <div className="ab-dir-text ab-rv" style={{ "--d": ".15s" }}>
            <div className="ab-dir-quote"><Icon name="quote" size={44} /></div>
            <h2>A Message from Our Director</h2>
            {directorMessage.map((para) => (
              <p key={para}>{para}</p>
            ))}
            <div className="ab-dir-sign">
              <div>
                <strong>{DIRECTOR.name}</strong>
                <span>{DIRECTOR.title}</span>
              </div>
            </div>
            <div className="ab-dir-focus">
              {directorFocus.map((f) => (
                <div className="ab-dir-chip" key={f.code}>
                  <Icon name={f.icon} size={26} />
                  <div><b>{f.label}</b><small>{f.code}</small></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VISION PILLARS */}
      <section className="ab-vision">
        <div className="container">
          <div className="ab-head center">
            <h2>Every Property, Looked After Properly</h2>
            <p>The three things we aim to deliver for every client.</p>
          </div>
          <div className="ab-vision-grid">
            {vision.map((v, i) => (
              <div className="ab-vision-item ab-rv" key={v.title} style={{ "--d": `${i * 0.12}s` }}>
                <div className="ab-ico"><Icon name={v.icon} size={26} /></div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CLIENTS TRUST US */}
      <section className="ab-section container">
        <div className="ab-head">
          <h2>Why Clients Trust Us</h2>
          <p>One dependable team for your letting, maintenance and cleaning.</p>
        </div>
        <div className="ab-trust">
          <div className="ab-trust-img ab-rv">
            <img src={images.team} alt="Our vetted and insured team" />
          </div>
          <div className="ab-trust-list">
            {trust.map((t, i) => (
              <div className="ab-trust-card ab-rv" key={t.title} style={{ "--d": `${i * 0.1}s` }}>
                <div className="ab-ico"><Icon name={t.icon} size={26} /></div>
                <div>
                  <h3>{t.title}</h3>
                  <p>{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section
        className="ab-cta"
        style={{ backgroundImage: bg("linear-gradient(rgba(15,34,56,.86), rgba(15,34,56,.86))", images.cta) }}
      >
        <div className="container">
          <h2>Ready to take the hassle out of property care?</h2>
          <p>Let's discuss your residential or commercial property needs today.</p>
          <div className="ab-cta-btns">
            <Link to="/contact" className="btn btn-gold">Contact Our Team</Link>
            <a href="tel:07459262621" className="btn btn-outline"><Icon name="phone" size={18} />07459 262621</a>
          </div>
        </div>
      </section>
    </>
  );
}